import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Palmtree,
  HeartPulse,
  Baby,
  BadgeCheck,
  BadgeMinus,
  Plus,
  Funnel,
  RefreshCw,
  Inbox } from
"lucide-react";

import { AppLayout, PageBackLink } from "../../../components/layout";
import {
  ContentCard,
  EmptyState,
  LoadingState,
  PageHeader,
  ui } from
"../../../components/ui";
import {
  WithdrawDialog,
  RevokeLeaveModal,
  LeaveActionToast,
  ManagerRemarksCell,
  EmployeeLeaveFiltersModal,
  EmployeeLeaveMobileList,
  LeaveDetailsModal,
  LeaveStatusBadge,
  TablePagination,
  countEmployeeActiveFilters,
  getDefaultEmployeeLeaveFilters } from
"../components";
import { getEmployeeLeaveRowAction } from "../components/employeeLeaveRowAction";
import { leaveService } from "../../../services/leaveService";
import sessionDB from "../../../utils/sessionDB";
import { mapLeaveRecords } from "../../../lib/leaveRecords";
import { buildSuccessToast } from "../../../lib/leaveApiMessages";

const PAGE_SIZE = 10;

export function LeavePortal() {
  const navigate = useNavigate();
  const [withdrawId, setWithdrawId] = useState(null);
  const [revokeRequest, setRevokeRequest] = useState(null);
  const [toast, setToast] = useState(null);
  const [rows, setRows] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [, setRawRequests] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(getDefaultEmployeeLeaveFilters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const sessionEmpId = Number(sessionDB.getEmpId());
      const optionalEmpId =
      Number.isFinite(sessionEmpId) && sessionEmpId > 0 ? sessionEmpId : undefined;

      const [typesResult, recordsResult, balancesResult] = await Promise.allSettled([
      leaveService.getLeaveTypes(),
      leaveService.getEmployeeLeavesByStatus(optionalEmpId, { size: 10 }),
      leaveService.getLeaveBalance(optionalEmpId)]
      );

      const types = typesResult.status === "fulfilled" ? typesResult.value : [];
      const records = recordsResult.status === "fulfilled" ? recordsResult.value : [];
      const balances = balancesResult.status === "fulfilled" ? balancesResult.value : [];

      if (typesResult.status === "rejected") {
        console.error("Error loading leave types:", typesResult.reason);
      }
      if (recordsResult.status === "rejected") {
        console.error("Error loading leave requests:", recordsResult.reason);
      }
      if (balancesResult.status === "rejected") {
        console.error("Error loading leave balances:", balancesResult.reason);
      }

      setLeaveTypes(Array.isArray(types) ? types : []);
      setRawRequests(records);
      setLeaveBalances(Array.isArray(balances) ? balances : []);

      const mapped = mapLeaveRecords(records, types);
      mapped.sort((a, b) => b.appliedOnTime - a.appliedOnTime);
      setRows(mapped);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const visibleRows = useMemo(() => {
    let out = [...rows];
    const { leaveType, status, dateRange } = filters;

    if (leaveType !== "all") {
      out = out.filter((row) => String(row.leaveTypeId) === String(leaveType));
    }

    if (status !== "all") {
      out = out.filter((row) => row.status?.toLowerCase() === status.toLowerCase());
    }

    if (dateRange?.from) {
      out = out.filter((row) => {
        const rowDate = row.fromDate ? new Date(row.fromDate) : new Date(row.from);
        return rowDate >= dateRange.from;
      });
    }

    if (dateRange?.to) {
      out = out.filter((row) => {
        const rowDate = row.toDate ? new Date(row.toDate) : new Date(row.to ?? row.from);
        return rowDate <= dateRange.to;
      });
    }

    return out;
  }, [rows, filters]);

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return visibleRows.slice(start, start + PAGE_SIZE);
  }, [visibleRows, page]);

  const activeFilterCount = countEmployeeActiveFilters(filters);

  const handleResetFilters = () => {
    setFilters(getDefaultEmployeeLeaveFilters());
    setPage(1);
  };

  const handleApplyFilters = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleWithdraw = async (id) => {
    try {
      setLoading(true);
      const response = await leaveService.withdrawLeaveRequest(Number(id));
      setWithdrawId(null);
      const successToast = buildSuccessToast(response, { includeRemarks: false });
      if (successToast) setToast(successToast);
      await loadDashboardData();
    } catch (err) {
      console.error("Failed to withdraw request", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeConfirm = async () => {




























    // Revoke is temporarily disabled — see RevokeLeaveModal "feature coming soon" UI.
    /*
    const handleRevokeConfirm = async (reason, checked) => {
      if (!revokeRequest) return;
      try {
        setLoading(true);
        const response = await leaveService.revokeLeaveRequest(Number(revokeRequest.id), {
          revocationReason: reason,
          leavesDetail: Object.entries(checked)
            .filter(([, sessions]) => sessions.firstHalf || sessions.secondHalf)
            .map(([date, sessions]) => ({
              date,
              firstHalf: sessions.firstHalf ? "Y" : "N",
              secondHalf: sessions.secondHalf ? "Y" : "N",
            })),
        });
        setRevokeRequest(null);
        const successToast = buildSuccessToast(response, { includeRemarks: false });
        if (successToast) setToast(successToast);
        await loadDashboardData();
      } catch (err) {
        console.error("Failed to revoke leave request", err);
        throw err;
      } finally {
        setLoading(false);
      }
    };
    */};return <AppLayout overlay={<>
          <EmployeeLeaveFiltersModal open={filterModalOpen} onOpenChange={setFilterModalOpen} appliedFilters={filters} onApply={handleApplyFilters} leaveTypes={leaveTypes} />

          <LeaveDetailsModal open={!!selectedLeave} onOpenChange={(open) => !open && setSelectedLeave(null)} leave={selectedLeave} onWithdraw={(leave) => {setSelectedLeave(null);setWithdrawId(leave.id);}} onRevoke={(leave) => {setSelectedLeave(null);setRevokeRequest(leave);}} actionLoading={loading} />

          <WithdrawDialog open={!!withdrawId} onOpenChange={(v) => !v && setWithdrawId(null)} actionType="withdraw"
        onConfirm={() => {
          if (withdrawId) {
            handleWithdraw(withdrawId);
          }
        }} />
      

          <RevokeLeaveModal
        open={!!revokeRequest}
        onOpenChange={(v) => !v && setRevokeRequest(null)}
        leaveRequest={revokeRequest}
        onConfirm={handleRevokeConfirm} />
      

          <LeaveActionToast toast={toast} />
        </>
    }>
    
      <PageBackLink to="/hrms" />
          <PageHeader
      title={i18n.t("leave.leaveReport")}
      action={
      <button
        type="button"
        onClick={() => navigate("/hrms-ess/apply-leave")}
        className={ui.primaryBtn}>
        
                <Plus className="h-4 w-4" />
                <Trans i18nKey="formActions.applyLeave" />
              </button>
      } />
    

          {loading && rows.length === 0 ?
    <LoadingState message="Loading your dashboard details..." /> :

    <>
              <SummaryCards leaveTypes={leaveTypes} leaveBalances={leaveBalances} />
              <ContentCard
        className="mt-5"
        title={i18n.t("leave.myLeaveRequests")}
        action={
        <div className="flex items-center gap-1.5 shrink-0">
                    <button
            type="button"
            onClick={() => setFilterModalOpen(true)}
            aria-label={i18n.t("formActions.filterLeaveRequests2")}
            className={ui.filterBtn}>
            
                      <Funnel className="h-4 w-4 shrink-0" />
                      <span className="hidden min-[400px]:inline"><Trans i18nKey="formActions.filter" /></span>
                      {activeFilterCount > 0 &&
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-primary)] px-1.5 text-[11px] font-bold text-white">
                          {activeFilterCount}
                        </span>
            }
                    </button>
                    <button
            type="button"
            onClick={handleResetFilters}
            disabled={activeFilterCount === 0}
            aria-label={i18n.t("formActions.resetFilters2")}
            className={`${ui.filterBtn} disabled:opacity-50 disabled:cursor-not-allowed`}>
            
                      <RefreshCw className="h-4 w-4 shrink-0" />
                      <span className="hidden min-[400px]:inline"><Trans i18nKey="formActions.reset" /></span>
                    </button>
                  </div>
        }>
        
                {visibleRows.length === 0 ?
        <EmptyState
          icon={Inbox}
          title={i18n.t("leave.noLeaveRequestsFound")}
          message="Try adjusting filters or apply for a new leave." /> :


        <>
                    <EmployeeLeaveMobileList
            rows={pagedRows}
            onSelect={setSelectedLeave} />
          
                    <RequestsTable
            rows={pagedRows}
            onSelect={setSelectedLeave}
            onWithdraw={(id) => setWithdrawId(id)}
            onRevoke={(req) => setRevokeRequest(req)} />
          
                    <TablePagination
            page={page}
            totalPages={totalPages}
            totalItems={visibleRows.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage} />
          
                  </>
        }
              </ContentCard>
            </>
    }
    </AppLayout>;

}

const CHART_COLORS = {
  blue: "#1E3383",
  cyan: "#A1EEF9",
  teal: "#22D1EE",
  green: "#22C55E",
  amber: "#F59E0B",
  purple: "#8B5CF6",
  pink: "#EC4899"
};

const CHART_PALETTE = [
CHART_COLORS.blue,
CHART_COLORS.teal,
CHART_COLORS.green,
CHART_COLORS.amber,
CHART_COLORS.purple,
CHART_COLORS.pink,
CHART_COLORS.cyan];


function withAlpha(hex, alpha) {
  const normalized = hex.replace("#", "");
  const value = Number.parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function SummaryCards({ leaveTypes = [], leaveBalances = [] }) {
  const getCardStyle = (code, index = 0) => {
    switch (code?.toUpperCase()) {
      case "AL":
        return {
          icon: CalendarDays,
          progressColor: CHART_COLORS.blue
        };
      case "EL":
        return {
          icon: BadgeCheck,
          progressColor: CHART_COLORS.teal
        };
      case "CL":
        return {
          icon: Palmtree,
          progressColor: CHART_COLORS.green
        };
      case "SL":
        return {
          icon: HeartPulse,
          progressColor: CHART_COLORS.amber
        };
      case "ML":
        return {
          icon: Baby,
          progressColor: CHART_COLORS.pink
        };
      default:
        return {
          icon: BadgeMinus,
          progressColor: CHART_PALETTE[index % CHART_PALETTE.length]
        };
    }
  };

  const cards = leaveTypes.map((type, index) => {
    const entry =
    leaveBalances.find((balance) => String(balance.leaveTypeId) === String(type.typeId)) ??
    leaveBalances.find(
      (balance) =>
      type.leaveCode &&
      balance.leaveCode &&
      String(balance.leaveCode).toUpperCase() === String(type.leaveCode).toUpperCase()
    ) ??
    null;
    const style = getCardStyle(type.leaveCode ?? entry?.leaveCode, index);
    const leavesApproved = entry?.leavesApproved ?? 0;
    const totalDays = entry?.total ?? 0;

    return {
      key: type.typeId ?? type.leaveCode ?? index,
      title: type.leaveName || entry?.leaveName || "Leave",
      value: leavesApproved,
      total: totalDays,
      icon: style.icon,
      progressColor: style.progressColor
    };
  });

  if (cards.length === 0) {
    return (
      <p className="text-sm text-[var(--color-text-secondary)]">
        No active leave types available.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3.5 auto-rows-fr">
      {cards.map((c) => {
        return (
          <div
            key={c.key}
            className="bg-white rounded-xl p-2.5 sm:p-3 shadow-[0_10px_24px_-20px_rgba(30,51,131,0.35)] border border-white/60 flex flex-col justify-between min-h-[68px]">
            
            <div className="flex items-center justify-between gap-1.5">
              <h3 className="text-[11px] sm:text-[12px] font-semibold text-[var(--color-text-secondary)] leading-tight truncate min-w-0">
                {c.title}
              </h3>
              <div
                className="h-6 w-6 shrink-0 rounded-md grid place-items-center"
                style={{ backgroundColor: withAlpha(c.progressColor, 0.12) }}>
                
                <c.icon className="h-3.5 w-3.5" style={{ color: c.progressColor }} />
              </div>
            </div>
            <div className="flex items-baseline gap-0.5 pt-1">
              <span className="text-[22px] sm:text-[24px] font-bold text-foreground leading-none">
                {Number(c.value).toFixed(1)}
              </span>
              <span className="text-[12px] sm:text-[13px] font-medium text-[var(--color-text-secondary)] leading-none">
                /{Number(c.total).toFixed(1)}
              </span>
            </div>
          </div>);

      })}
    </div>);

}

function RequestsTable({ rows, onSelect, onWithdraw, onRevoke }) {
  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]">
        <thead>
          <tr className={ui.tableHead}>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leaveId" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leaveType" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leavePeriod" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="dateTime.days" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="status.status" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="common.managerRemarks" /></th>
            <th className="px-4 py-3 font-semibold text-right whitespace-nowrap"><Trans i18nKey="common.action" /></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-text-border)]/35">
          {rows.map((r) =>
          <tr
            key={r.id}
            onClick={() => onSelect?.(r)}
            className="hover:bg-[var(--color-workspace)]/20 transition-colors cursor-pointer">
            
              <td className="px-4 py-3.5 font-semibold text-[var(--color-primary)] whitespace-nowrap">#{r.leaveId ?? r.id}</td>
              <td className="px-4 py-3.5 font-semibold text-foreground whitespace-nowrap">{r.type}</td>
              <td className="px-4 py-3.5 text-[var(--color-text-secondary)] whitespace-nowrap">{r.dateRange}</td>
              <td className="px-4 py-3.5 whitespace-nowrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[var(--color-workspace)]/60 text-[var(--color-primary)] text-xs font-semibold">
                  {r.days} {r.days === 1 ? "Day" : "Days"}
                </span>
              </td>
              <td className="px-4 py-3.5 whitespace-nowrap"><LeaveStatusBadge status={r.status} /></td>
              <td className="px-4 py-3.5 text-[var(--color-text-secondary)] max-w-[220px]">
                <ManagerRemarksCell remarks={r.actionByRemarks} />
              </td>
              <td className="px-4 py-3.5 text-right whitespace-nowrap">
                {getEmployeeLeaveRowAction(r.status) === "withdraw" ?
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onWithdraw(r.id);
                }}
                className="inline-flex items-center justify-center h-8 px-2.5 rounded-lg border border-[var(--color-error)]/30 text-[var(--color-error)] hover:bg-[var(--color-error)] hover:text-white text-[11px] font-semibold transition cursor-pointer">
                    <Trans i18nKey="formActions.withdraw" />
                  
              </button> :

              getEmployeeLeaveRowAction(r.status) === "revoke" ?
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onRevoke(r);
                }}
                className="inline-flex items-center justify-center h-8 px-2.5 rounded-lg border border-[var(--color-warning)]/40 text-[var(--color-warning)] hover:bg-[var(--color-warning)] hover:text-white text-[11px] font-semibold transition cursor-pointer">
                    <Trans i18nKey="formActions.revoke" />
                  
              </button> :


              <span className="text-[var(--color-text-secondary)]">—</span>
              }
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>);

}
